import { ChangeDetectionStrategy, Component, DestroyRef, input, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Observable, switchMap, tap } from 'rxjs';
import { OrderNotesState, OrderNotesStore } from './order-notes.store';

@Component({
  selector: 'order-notes',
  templateUrl: './order-notes.component.html',
  styleUrl: './order-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, DatePipe]
})
// The bom-notes widget for a zlecenie (spec 0045) — a copy on purpose, not a shared "notes for anything" component.
// The card follows the orderId input: every change re-inits the store (switchMap drops stale in-flight responses)
export class OrderNotesComponent implements OnDestroy {
  readonly orderId = input.required<number | undefined>();

  readonly state$: Observable<OrderNotesState>;

  readonly composerOpen = signal(false);

  constructor(
    private store: OrderNotesStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    toObservable(this.orderId)
      .pipe(
        // a composer opened before the zlecenie existed must not post to it unasked
        tap(() => this.composerOpen.set(false)),
        switchMap(orderId => this.store.init(orderId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  // Called from the card header's "+" icon — it lives in the page markup, like the BOM page's NOTATKI card
  toggleComposer(): void {
    this.composerOpen.update(open => !open);
  }

  closeComposer(): void {
    this.composerOpen.set(false);
  }

  onSaveClick(content: string): void {
    const orderId = this.orderId();
    const trimmed = content.trim();

    if (!trimmed || orderId === undefined) {
      return;
    }

    this.store.create(orderId, trimmed).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // closing removes the textarea and its content with it — kept open (with content) on error
      if (!this.store.state.saveError) {
        this.composerOpen.set(false);
      }
    });
  }
}
